import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray, FormikProps } from "formik";
import * as Yup from "yup";

// MUI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Add from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";

import FormikControl from "../../formik/FormikControl";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../redux/hooks/redux-hooks";
import { AddEdit } from "../../../SectionWiseClassification";
import { setBidCreationCustomerPOCs } from "../../../redux/actions/CustomerPOCActions";
import { isValidArr } from "../../../utility/utils";

type Props = {
  type: AddEdit;
};

type CustomerObjType = {
  name: string;
  designation: string;
  email: string;
  phone: string;
};

const initialCustomersObj: CustomerObjType = {
  name: "",
  designation: "",
  email: "",
  phone: "",
};

const validationSchema = Yup.object({
  customers: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string()
          .required("Required")
          .max(200, "Name should be less than 200 charaters"),
        designation: Yup.string()
          .required("Required")
          .max(200, "Designation should be less than 200 charaters"),
        email: Yup.string()
          .email("Invalid email format")
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
          .required("Required"),
        phone: Yup.string().required("Required"),
      })
    )
    .required(),
});

type FormProps = Yup.InferType<typeof validationSchema>;

const initialValues: FormProps = {
  customers: [initialCustomersObj],
};

const CustomerPOCCreationSection = ({ type }: Props) => {
  const dispatch = useAppDispatch();
  const [formState, setFormState] = useState<FormProps>(initialValues);

  // Customer POCs list on creating new bid
  const bidCreationCustomerPOCs = useAppSelector(
    (state) => state.projectCustomerPOCs.bidCreationCustomerPOCs
  );

  // Customer POCs list getting from the api on edit
  const projectCustomerPOCs = useAppSelector(
    (state) => state.projectCustomerPOCs.allCustomerPOCs
  );

  // To set the initial state on edit bid with the data from api
  useEffect(() => {
    if (
      type === "edit" &&
      isValidArr(projectCustomerPOCs) &&
      !isValidArr(bidCreationCustomerPOCs)
    ) {
      dispatch(setBidCreationCustomerPOCs(projectCustomerPOCs));
    }
  }, [projectCustomerPOCs, bidCreationCustomerPOCs, type, dispatch]);

  // To set the state from redux while returning back to the section
  useEffect(() => {
    if (isValidArr(bidCreationCustomerPOCs) && !formState.customers[0].name) {
      setFormState(() => ({
        customers: bidCreationCustomerPOCs.map((rec) => ({
          name: rec.name,
          designation: rec.designation,
          email: rec.emailId,
          phone: rec.phoneNumber,
        })),
      }));
    }
  }, [bidCreationCustomerPOCs, formState]);

  const handleSubmit = () => {
    console.log("submitting");
  };

  const handleRowRemove = (
    remove: <T>(index: number) => T | undefined,
    index: number
  ) => {
    // Remove data from the redux store
    if (bidCreationCustomerPOCs[index]) {
      const activeFormState = [...bidCreationCustomerPOCs];
      activeFormState.splice(index, 1);
      dispatch(setBidCreationCustomerPOCs(activeFormState));
    }
    remove(index);
  };

  const handleRowAdd = (
    push: <R extends {}>(obj: R) => void,
    formik: FormikProps<FormProps>
  ) => {
    const lastIndex = formik.values.customers.length - 1;
    Object.keys(initialCustomersObj).forEach((fieldName) => {
      formik.setFieldTouched(`customers[${lastIndex}].${fieldName}`, true);
    });

    if (
      Object.keys(formik.errors).length === 0 &&
      formik.values.customers[lastIndex].name
    ) {
      // Add existing form data to the redux store
      const activeFormState = formik.values.customers.map((rec) => ({
        projectId: 0,
        name: rec.name,
        designation: rec.designation,
        emailId: rec.email,
        phoneNumber: rec.phone,
      }));
      dispatch(setBidCreationCustomerPOCs(activeFormState));
      push(initialCustomersObj);
    }
  };

  // To add, update or remove the redux state for customer POC section
  const updateIndexStateOnChange = (data: CustomerObjType, index: number) => {
    if (data.name && data.designation && data.email && data.phone) {
      const activeFormState = {
        projectId: 0,
        name: data.name,
        designation: data.designation,
        emailId: data.email,
        phoneNumber: data.phone,
      };
      if (bidCreationCustomerPOCs[index]) {
        const customerPOCs = [...bidCreationCustomerPOCs];
        customerPOCs.splice(index, 1, activeFormState);
        dispatch(setBidCreationCustomerPOCs(customerPOCs));
      } else {
        dispatch(
          setBidCreationCustomerPOCs([
            ...bidCreationCustomerPOCs,
            activeFormState,
          ])
        );
      }
    } else {
      if (bidCreationCustomerPOCs[index]) {
        // Remove data from the redux store
        const customerPOCs = [...bidCreationCustomerPOCs];
        customerPOCs.splice(index, 1);
        dispatch(setBidCreationCustomerPOCs(customerPOCs));
      }
    }
  };

  return (
    <Box
      sx={{
        padding: "1em",
        border: "1px solid rgb(0 0 0 / 18%)",
        borderRadius: "0.375rem",
      }}
    >
      <Formik
        initialValues={formState}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <FieldArray name="customers">
              {({ remove, push }) => (
                <Box>
                  <Grid container spacing={2}>
                    {formik.values.customers?.map((row, index) => {
                      return (
                        <React.Fragment key={index}>
                          <Grid item xs={6} sm={3}>
                            <FormikControl
                              control="input"
                              type="text"
                              size="small"
                              label="Name"
                              fullWidth
                              required
                              value={row.name || ""}
                              name={`customers[${index}].name`}
                              placeholder="Name"
                              changeHandler={() =>
                                updateIndexStateOnChange(row, index)
                              }
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormikControl
                              control="input"
                              type="text"
                              size="small"
                              label="Designation"
                              fullWidth
                              required
                              value={row.designation || ""}
                              name={`customers[${index}].designation`}
                              placeholder="Designation"
                              changeHandler={() =>
                                updateIndexStateOnChange(row, index)
                              }
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormikControl
                              control="input"
                              type="email"
                              size="small"
                              label="Email"
                              fullWidth
                              required
                              value={row.email || ""}
                              name={`customers[${index}].email`}
                              placeholder="Email"
                              changeHandler={() =>
                                updateIndexStateOnChange(row, index)
                              }
                            />
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <FormikControl
                              info="Please enter country code"
                              control="input"
                              type="text"
                              size="small"
                              label="Phone"
                              fullWidth
                              required
                              value={row.phone || ""}
                              name={`customers[${index}].phone`}
                              placeholder="Phone"
                              changeHandler={() =>
                                updateIndexStateOnChange(row, index)
                              }
                            />
                          </Grid>
                          <Grid item xs={2} sm={1} alignSelf="end">
                            <Stack direction="row">
                              {formik.values.customers.length !== 1 && (
                                <IconButton
                                  onClick={() => handleRowRemove(remove, index)}
                                >
                                  <DeleteOutline color="error" />
                                </IconButton>
                              )}
                              {formik.values.customers.length - 1 === index && (
                                <IconButton
                                  onClick={() => handleRowAdd(push, formik)}
                                >
                                  <Add color="primary" />
                                </IconButton>
                              )}
                            </Stack>
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CustomerPOCCreationSection;
